import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  type ProjectSkillVersionDTO,
  type SkillTemplateListItemDTO,
  fetchSkillTemplates,
  fetchSkills,
} from '@/api/skills/api';

export type { ProjectSkillVersionDTO, SkillTemplateListItemDTO } from '@/api/skills/api';

export type SkillGridItem =
  | {
      kind: 'template';
      templateKey: string;
      title: string;
      description: string;
      author: string | null;
      tags: string[];
      metaVersion: string | null;
    }
  | { kind: 'user'; row: ProjectSkillVersionDTO };

export interface UseSkillsListResult {
  loading: boolean;
  error: string | null;
  templates: SkillTemplateListItemDTO[];
  persisted: ProjectSkillVersionDTO[];
  gridItems: SkillGridItem[];
  load: () => Promise<void>;
}

/**
 * Fetches bundled skill templates and persisted user skill versions,
 * then derives the merged grid-item list used by the skills library page.
 */
export function useSkillsList(accessToken: string | null | undefined): UseSkillsListResult {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<SkillTemplateListItemDTO[]>([]);
  const [persisted, setPersisted] = useState<ProjectSkillVersionDTO[]>([]);

  const load = useCallback(async () => {
    if (!accessToken) {
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const [templatesResult, skillsResult] = await Promise.allSettled([
        fetchSkillTemplates(accessToken),
        fetchSkills(accessToken),
      ]);
      if (templatesResult.status === 'fulfilled') {
        setTemplates(templatesResult.value);
      } else {
        setTemplates([]);
        const reason =
          templatesResult.reason instanceof Error
            ? templatesResult.reason.message
            : 'Failed to load templates';
        setError(reason);
      }
      if (skillsResult.status === 'fulfilled') {
        setPersisted(skillsResult.value.skills);
      } else {
        setPersisted([]);
        const reason =
          skillsResult.reason instanceof Error
            ? skillsResult.reason.message
            : 'Failed to load saved skills';
        setError((prev) => (prev ? `${prev} · ${reason}` : reason));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const gridItems: SkillGridItem[] = useMemo(() => {
    const templateItems: SkillGridItem[] = templates
      .slice()
      .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }))
      .map((item) => ({
        kind: 'template' as const,
        templateKey: item.template_key,
        title: item.title,
        description: item.description ?? '',
        author: item.author ?? null,
        tags: item.tags ?? [],
        metaVersion: item.meta_version ?? null,
      }));
    const userItems: SkillGridItem[] = persisted
      .slice()
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .map((row) => ({ kind: 'user' as const, row }));
    return [...templateItems, ...userItems];
  }, [templates, persisted]);

  return { loading, error, templates, persisted, gridItems, load };
}
